import { useMemo } from "react";
import { uniqueId } from "lodash";

import { InputWithIcon } from "@/DesignSystem/Molecules/ToolBox/InputWithIcon";
import { TextFieldContainer } from "../Components/TextFieldContainer";

type TextInputLabelPosition = "top" | "left";
type TextInputCtaPosition = "top" | "bottom" | "none";

interface ITextInputProps {
    /**
     * The optional text input ID.
     */
    id?: string;

    /**
     * An ID to target in frontend tests.
     */
    dataCy?: string;

    /**
     * Whether to disable the text input.
     */
    disabled?: boolean;

    /**
     * Automatically focuses the input field when the component is mounted.
     */
    autoFocus?: boolean;

    /**
   * The width of the TextInput in pixels.
   */
    width?: string;

    /**
   * Label text displayed for the TextInput: can be positioned on top, left, or omitted.
   */
    label?: string;

    /**
   * Position of the label: 'top' or 'left'.
   */
    labelPosition?: TextInputLabelPosition;

    /**
   * To indicate whether the field is required. If true, displays a red asterisk next to the label.
   */
    required?: boolean;

    /**
   * Placeholder text inside the input when empty.
   */
    placeholder?: string;

    /**
   * The value of the input.
   */
    value?: string;

    /**
   * Default value of the input.
   */
    defaultValue?: string;

    /**
   * Hint text displayed below the input.
   */
    hintText?: string;

    /**
    * Error message text displayed below the input, instead of the hint.
    * This is rendered only when hasError is true and this has a value.
    */
    errorMessage?: string;

    /**
     * Indicates whether the input should display error styling.
     * If true, a red border is applied to the input field.
     */
    hasError?: boolean;

    /**
   * CTA link button displayed above, below, or omitted.
   */
    ctaButton?: string;

    /**
   * Position of the CTA link button: 'top', 'bottom', or 'none'.
   */
    ctaButtonPosition?: TextInputCtaPosition;

    /**
   * The tab order of the text input.
   */
    tabIndex?: number;

    /**
   * Maximum length of the input text.
   */
    maxLength?: number;

    /**
   * The name of the text input.
   */
    name?: string;

    /**
     * The input type
     */
    type?: "text" | "password";

    /**
   * Function triggered when the input value changes.
   */
    onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;

    /**
   * Callback function triggered when the input field loses focus.
   */
    onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;

    /**
   * Callback function when the CTA link button is clicked.
   */
    onCtaClick?: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
}

/**
 * The text input.
 * @param {ITextInputProps} param The props used to render the text input.
 * @returns The rendered component.
 */
function TextInput({
    id,
    dataCy,
    disabled,
    autoFocus,
    width,
    label,
    labelPosition = "top",
    required,
    placeholder,
    value,
    defaultValue,
    hintText,
    errorMessage,
    hasError,
    ctaButton,
    ctaButtonPosition = "none",
    tabIndex,
    maxLength,
    name,
    type = "text",
    onChange,
    onBlur,
    onCtaClick
}: Readonly<ITextInputProps>): JSX.Element {
    const inputId = useMemo(() => id ?? `text-input-tooltip${uniqueId()}`, []);
    const errorMessageToRender = hasError ? errorMessage : null;
    const errorMessageId = errorMessageToRender ? `errorMessage-${uniqueId()}` : undefined;

    return (
        <TextFieldContainer
            id={inputId}
            dataCy={dataCy}
            width={width}
            label={label}
            labelPosition={labelPosition}
            required={required}
            ctaButton={ctaButton}
            ctaButtonPosition={ctaButtonPosition}
            onCtaClick={onCtaClick}
            hintText={hintText}
            errorMessage={errorMessageToRender}
            errorMessageId={errorMessageId}
        >
            <InputWithIcon
                id={inputId}
                errorMessageId={errorMessageId}
                hasError={hasError}
                placeholder={placeholder}
                value={value}
                defaultValue={defaultValue}
                onChange={onChange}
                onBlur={onBlur}
                disabled={disabled}
                autoFocus={autoFocus}
                required={required}
                tabIndex={tabIndex}
                maxLength={maxLength}
                name={name}
                type={type}
                iconPosition="right"
                useHoverToolTip
            />
        </TextFieldContainer>
    );
}

export { TextInput, TextInputCtaPosition, TextInputLabelPosition };
